import { useEffect, useState } from "react";
import { Button } from "../ui/button.js";
import { Switch } from "../ui/switch.js";
import { useAuthSession, useWorkspaceRole } from "../hooks/useWorkspaceRole.js";

const MILLICENTS_PER_DOLLAR = 100_000;

const FALLBACK_TIMEZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Kolkata",
  "Asia/Tokyo",
  "Australia/Sydney",
];

function supportedTimezones(): string[] {
  try {
    const intlWithValues = Intl as typeof Intl & {
      supportedValuesOf?: (key: "timeZone") => string[];
    };
    const zones = intlWithValues.supportedValuesOf?.("timeZone") ?? [];
    return zones.length > 0 ? zones : FALLBACK_TIMEZONES;
  } catch {
    return FALLBACK_TIMEZONES;
  }
}

interface DailyBrief {
  enabled: boolean;
  publishTime: string;
  weekdaysOnly: boolean;
}

interface WorkspaceSettings {
  name: string;
  timezone: string;
  dailyBrief: DailyBrief;
  dailyCeilingMillicents: number;
  /** "Daily Bailey readings": the workspace's daily scoping cap (`scopingLimits.ts`). */
  scopingSessionsPerDay?: number;
  /** `unreadable` means the shown number is the default, not the workspace's: never saved over. */
  scopingSessionsPerDaySource?: "stored" | "default" | "invalid" | "unreadable";
  scopingSessionsPerDayDefault?: number;
  scopingSessionsPerDayMax?: number;
  /** What the row holds when it was set above the most before the most came down; it reads as the most. */
  scopingSessionsPerDayStoredAboveMax?: number;
}

const DEFAULT_DAILY_READINGS = 20;
const MAX_DAILY_READINGS = 100;

export interface ServedModels {
  asOf: string;
  tiers: Array<{ tier: string; provider: string; model: string; heldUntil: string | null }>;
}

/** "Weekdays at 6:00", from the Brief's morning slot. */
export function briefCadence(brief: DailyBrief): string {
  const [hour, minute] = brief.publishTime.split(":");
  const time = `${Number(hour)}:${minute ?? "00"}`;
  return `${brief.weekdaysOnly ? "Weekdays" : "Every day"} at ${time}`;
}

/** The field's text for the stored daily readings cap: the default when an older backend sends none. */
function readingsOf(workspace: WorkspaceSettings): string {
  return String(
    workspace.scopingSessionsPerDay ??
      workspace.scopingSessionsPerDayDefault ??
      DEFAULT_DAILY_READINGS,
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** The distinct model names serving the workspace, in tier order. */
export function servedModelNames(served: ServedModels): string[] {
  return [...new Set(served.tiers.map((tier) => tier.model))];
}

/** The latest date a hold keeps any tier, or null when nothing is held. */
export function heldUntil(served: ServedModels): string | null {
  const dates = served.tiers
    .map((tier) => tier.heldUntil)
    .filter((date): date is string => date !== null)
    .sort();
  return dates.at(-1) ?? null;
}

/**
 * Settings, the Workspace card: the handful of things that are the workspace's rather
 * than a Team's. Name and timezone, the Daily Brief as one switch, the daily
 * spend ceiling, and the models serving the workspace as a dated fact with a
 * 30-day hold (superadmins only). This device's colour scheme is on You.
 * Everything about how a Team runs lives on that Team's page.
 */
export function SettingsView() {
  const session = useAuthSession();
  const role = useWorkspaceRole();
  const workspaceId = session?.workspace.id ?? "";
  // The backend takes these writes from the owner alone; the page says so
  // rather than letting a Save fail.
  const canEdit = role === "owner" || session?.user.isSuperadmin === true;
  // Which models serve the workspace is ours to know, never a customer's:
  // the Models row exists for superadmins alone, and so does its endpoint.
  const isSuperadmin = session?.user.isSuperadmin === true;
  const timezoneOptions = useState(supportedTimezones)[0];

  const [settings, setSettings] = useState<WorkspaceSettings | null>(null);
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("");
  const [ceiling, setCeiling] = useState("");
  const [readings, setReadings] = useState("");
  const [saving, setSaving] = useState<"idle" | "saving" | "saved">("idle");
  const [error, setError] = useState<string | null>(null);
  const [served, setServed] = useState<ServedModels | null>(null);
  const [holding, setHolding] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    const base = `/api/workspaces/${encodeURIComponent(workspaceId)}`;
    void (async () => {
      try {
        const [settingsRes, modelsRes] = await Promise.all([
          fetch(`${base}/settings`, { credentials: "include" }),
          isSuperadmin ? fetch(`${base}/models`, { credentials: "include" }) : null,
        ]);
        if (cancelled) return;
        if (settingsRes.ok) {
          const { workspace } = (await settingsRes.json()) as { workspace: WorkspaceSettings };
          if (cancelled) return;
          setSettings(workspace);
          setName(workspace.name);
          setTimezone(workspace.timezone);
          setCeiling(String(workspace.dailyCeilingMillicents / MILLICENTS_PER_DOLLAR));
          setReadings(readingsOf(workspace));
        }
        if (modelsRes?.ok) {
          const models = (await modelsRes.json()) as ServedModels;
          if (!cancelled) setServed(models);
        }
      } catch {
        if (!cancelled) setError("Workspace settings could not be read. Refresh to try again.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, isSuperadmin]);

  async function patch(body: Record<string, unknown>): Promise<boolean> {
    try {
      const res = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/settings`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) return true;
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      setError(data.error ?? "The change was not saved.");
    } catch {
      setError("The change was not saved.");
    }
    return false;
  }

  async function toggleBrief(enabled: boolean) {
    if (!settings) return;
    setError(null);
    const previous = settings.dailyBrief;
    setSettings({ ...settings, dailyBrief: { ...previous, enabled } });
    if (!(await patch({ dailyBrief: enabled }))) {
      setSettings({ ...settings, dailyBrief: previous });
    }
  }

  const readingsMax = settings?.scopingSessionsPerDayMax ?? MAX_DAILY_READINGS;
  const readingsDefault = settings?.scopingSessionsPerDayDefault ?? DEFAULT_DAILY_READINGS;
  // An unreadable store shows the default, which is not the workspace's
  // number: the field stays shut so a Save never lowers the real cap.
  const readingsUnreadable = settings?.scopingSessionsPerDaySource === "unreadable";

  async function save(event: React.FormEvent) {
    event.preventDefault();
    const dollars = Number(ceiling);
    if (ceiling.trim() === "" || !Number.isFinite(dollars) || dollars < 0) {
      setError("Enter the spend ceiling in dollars a day, or 0 for none.");
      return;
    }
    const perDay = Number(readings);
    const readingsChanged = settings !== null && readings.trim() !== readingsOf(settings);
    if (readingsChanged && (!Number.isInteger(perDay) || perDay < 1 || perDay > readingsMax)) {
      setError(`Enter the daily Bailey readings as a whole number from 1 to ${readingsMax}.`);
      return;
    }
    setError(null);
    setSaving("saving");
    const ok = await patch({
      name: name.trim(),
      timezone: timezone.trim(),
      dailyCeilingMillicents: Math.round(dollars * MILLICENTS_PER_DOLLAR),
      // Only a changed number is sent, so a Save of the name never stores the
      // default as the workspace's own cap.
      ...(readingsChanged && !readingsUnreadable ? { scopingSessionsPerDay: perDay } : {}),
    });
    if (ok && readingsChanged && settings) {
      // The saved number is within the most, so the old above-the-most note goes.
      const { scopingSessionsPerDayStoredAboveMax: _cleared, ...rest } = settings;
      setSettings({
        ...rest,
        scopingSessionsPerDay: perDay,
        scopingSessionsPerDaySource: "stored",
      });
    }
    setSaving(ok ? "saved" : "idle");
  }

  async function hold() {
    setHolding(true);
    setError(null);
    try {
      const res = await fetch(`/api/workspaces/${encodeURIComponent(workspaceId)}/models/hold`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        setError("The models could not be held. Try again.");
        return;
      }
      setServed((await res.json()) as ServedModels);
    } catch {
      setError("The models could not be held. Try again.");
    } finally {
      setHolding(false);
    }
  }

  const names = served ? servedModelNames(served) : [];
  const until = served ? heldUntil(served) : null;

  return (
    <form onSubmit={(event) => void save(event)} className="body" data-testid="settings-workspace">
      <div className="field">
        <label htmlFor="workspace-name">Name</label>
        <div className="in">
          <input
            id="workspace-name"
            value={name}
            disabled={!canEdit || !settings}
            onChange={(event) => {
              setName(event.target.value);
              setSaving("idle");
            }}
          />
        </div>
      </div>
      <div className="field">
        <label htmlFor="workspace-timezone">Timezone</label>
        <div className="in">
          <input
            id="workspace-timezone"
            list="workspace-timezone-options"
            value={timezone}
            disabled={!canEdit || !settings}
            onChange={(event) => {
              setTimezone(event.target.value);
              setSaving("idle");
            }}
          />
        </div>
        <datalist id="workspace-timezone-options">
          {timezoneOptions.map((zone) => (
            <option key={zone} value={zone} />
          ))}
        </datalist>
      </div>

      <div className="field" data-testid="settings-daily-brief">
        <label htmlFor="workspace-daily-brief">Daily Brief</label>
        <span className="sw">
          <Switch
            id="workspace-daily-brief"
            aria-label="Daily Brief"
            checked={settings?.dailyBrief.enabled ?? false}
            disabled={!canEdit || !settings}
            onCheckedChange={(enabled) => void toggleBrief(enabled)}
          />
          Send the Brief
        </span>
        <div className="help">
          What shipped, what is waiting on a person.
          {settings ? ` ${briefCadence(settings.dailyBrief)}.` : ""}
        </div>
      </div>

      <div className="field">
        <label htmlFor="workspace-ceiling">Spend ceiling</label>
        <div className="inline">
          <div className="in mono" style={{ maxWidth: 140 }}>
            <input
              id="workspace-ceiling"
              aria-label="Spend ceiling in dollars per day"
              inputMode="decimal"
              value={ceiling}
              disabled={!canEdit || !settings}
              onChange={(event) => {
                setCeiling(event.target.value);
                setSaving("idle");
              }}
            />
          </div>
          <span className="fact" style={{ fontSize: 11.5 }}>
            USD / day
          </span>
        </div>
        <div className="help">
          Every Agent on every Team stops for the day when the workspace's model cost reaches this.
          0 means no ceiling. Usage shows where it went.
        </div>
      </div>

      <div className="field" data-testid="settings-daily-readings">
        <label htmlFor="workspace-daily-readings">Daily Bailey readings</label>
        <div className="inline">
          <div className="in mono" style={{ maxWidth: 140 }}>
            <input
              id="workspace-daily-readings"
              aria-label="Daily Bailey readings"
              inputMode="numeric"
              value={readings}
              disabled={!canEdit || !settings || readingsUnreadable}
              onChange={(event) => {
                setReadings(event.target.value);
                setSaving("idle");
              }}
            />
          </div>
          <span className="fact" style={{ fontSize: 11.5 }}>
            tickets / day
          </span>
        </div>
        <div className="help">
          How many tickets Bailey checks against the code each day, across every Team, before Reeve
          plans them. Default {readingsDefault}, up to {readingsMax}.
          {readingsUnreadable
            ? " The current limit could not be read, so it cannot be changed right now."
            : ""}
          {settings?.scopingSessionsPerDayStoredAboveMax !== undefined
            ? ` This workspace was set to ${settings.scopingSessionsPerDayStoredAboveMax}, above the most, so it runs at ${readingsMax}.`
            : ""}
        </div>
      </div>

      {isSuperadmin && (
        <div className="field" data-testid="settings-models">
          <label>Models</label>
          <div className="inline">
            <span className="body-p" style={{ flex: 1, minWidth: 0 }}>
              {served && names.length > 0 ? (
                <>
                  Served as of {formatDate(served.asOf)}:{" "}
                  <span className="mono">{names.join(" · ")}</span>.{" "}
                  {until
                    ? `Held on these models until ${formatDate(until)}.`
                    : "Hold this workspace on today's models to sit out the next change."}
                </>
              ) : (
                "The models serving this workspace appear here once they can be read."
              )}
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              loading={holding}
              disabled={!served || names.length === 0}
              onClick={() => void hold()}
            >
              Hold for 30 days
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="err-line" style={{ color: "var(--incident)", fontSize: 12.5 }}>
          {error}
        </p>
      )}

      <div className="acts">
        {canEdit ? (
          <>
            <button
              type="button"
              className="btn quiet sm"
              disabled={!settings}
              onClick={() => {
                if (!settings) return;
                setName(settings.name);
                setTimezone(settings.timezone);
                setCeiling(String(settings.dailyCeilingMillicents / MILLICENTS_PER_DOLLAR));
                setReadings(readingsOf(settings));
                setError(null);
                setSaving("idle");
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn primary sm"
              disabled={!settings || saving === "saving"}
            >
              {saving === "saved" ? "Saved" : "Save"}
            </button>
          </>
        ) : (
          <span className="fact" style={{ fontSize: 12, marginRight: "auto" }}>
            Only the workspace owner changes these.
          </span>
        )}
      </div>
    </form>
  );
}
